import React, { useEffect, useState } from "react";
import {
  useAccount,
  useWriteContract,
  useWaitForTransactionReceipt
} from "wagmi";
import { useSubscriberProxyContract } from "../../useCoinageContract";
import NFTPill from "../NFTPill/NFTPill";
import { Button } from "../Actions";
import { MintSuccess } from "../MintSuccess/MintSuccess";
import { StepContent } from "../NetworkMint/Steps/Components";
import { ConnectWalletSection } from "../ConnectWalletSection/ConnectWalletSection";
import MintError from "../MintError/MintError";
import { useTransactionUrl } from "../../utils/buildTransactionUrl";
import { EmailSignup } from "../EmailSignup";
import { WrapMintButtonWithChangeChain } from "../SwitchNetworkButton";
import { subscriberMint } from "../../utils/api/mint";
import { getApi } from "../../utils/api/getApiBase";
import { parseEther } from "viem";
import { extractErrorData } from "../../utils/extractError";

const SubscriberMint: React.FC<{ connectButtonText?: string }> = ({
  connectButtonText
}) => {
  const { isDisconnected, address } = useAccount();
  const [status, setStatus] = useState<"loading" | "error" | "success" | null>(
    null
  );
  const [error, setError] = useState<{ message: string; call?: string }>({
    message: "",
    call: undefined
  });

  useEffect(() => {
    async function addToCaucusList() {
      await getApi(`add/${address}`);
    }
    if (status === "success") {
      addToCaucusList();
    }
  }, [status, address]);

  // const {
  //   data: mintData,
  //   write,
  //   isLoading
  // } = useContractWrite({
  //   ...useSubscriberProxyContract(),
  //   functionName: "mintSubscriber",
  //   onSettled(data, error) {
  //     if (error) {
  //       console.log({ error });
  //       setStatus("error");
  //       setError(extractErrorData(error));
  //     }
  //   }
  // });

  const contractDetails = useSubscriberProxyContract();
  const {
    writeContract,
    data: mintData,
    isPending
  } = useWriteContract({
    mutation: {
      onSettled(data, error) {
        if (error) {
          setStatus("error");
          setError(extractErrorData(error));
        }
      }
    }
  });

  const shouldShowLoading = isPending || status === "loading";
  const transactionUrl = useTransactionUrl(mintData);

  const { data: transactionReceipt, status: transactionStatus } =
    useWaitForTransactionReceipt({
      chainId: 1,
      hash: mintData
    });

  useEffect(() => {
    if (transactionStatus === "success" && transactionReceipt) {
      setStatus("success");
    }
  }, [transactionStatus, transactionReceipt]);

  const isSaleActive = true;

  const handleMint = async () => {
    setStatus("loading");
    if (!address) return;

    const { data, error } = await subscriberMint({
      address: address
    });

    if (error) {
      setStatus("error");
    }

    const { hash, signature } = data;

    const cost = parseEther("0.02");
    try {
      writeContract({
        ...contractDetails,
        functionName: "mintSubscriber",
        args: [hash, signature],
        value: cost
      });
    } catch (e) {
      console.log(e);
    }
  };

  if (status === "success")
    return (
      <div className="mb-8">
        <MintSuccess
          id="subscriberSuccess"
          description={
            <>
              <span className="text-coinage-black">
                <b className="text-coinage-orange">Welcome to the Coinage.</b>{" "}
                We’re so happy you’re here.
                <br /> Remember to send your +1 Subscriber pass to a friend.
              </span>
              <EmailSignup
                formClassName="flex flex-col items-center"
                title="Enter your email for updates and announcements."
                titleClassName="body1 mt-12 text-coinage-black"
                containerClassName="mb-8"
                subscribeLocation="Subscriber Mint"
              />
            </>
          }
          discordMessage={
            <>
              <span className="font-bold">Visit Discord</span> to join other
              Subscribers in your private channel. <br /> And{" "}
              <span className="font-bold">join Charmverse </span>to vote and
              draft proposals.
            </>
          }
        />
      </div>
    );

  return (
    <section className="p-12" suppressHydrationWarning>
      <StepContent title="Subscriber Membership Pass.">
        <NFTPill
          limit={1}
          title="Subscriber Pass"
          video="/subscriber/subscriber600.mp4"
          price={{ label: 0.02 }}
        />
        {isDisconnected ? (
          <ConnectWalletSection
            hideTitle
            connectButtonText={connectButtonText}
          />
        ) : (
          <WrapMintButtonWithChangeChain>
            <Button
              loading={shouldShowLoading}
              modifier="primary"
              disabled={!isSaleActive}
              analyticsProperty="mint subscriber nft"
              onClick={handleMint}>
              {isSaleActive
                ? "Mint Subscriber Membership Pass"
                : "Sale not active"}
            </Button>
          </WrapMintButtonWithChangeChain>
        )}

        <MintError
          mintData={mintData}
          transactionUrl={transactionUrl}
          error={error}
          show={status === "error"}
        />
      </StepContent>
    </section>
  );
};

export default SubscriberMint;
